import { PiTrash } from 'react-icons/pi'

/**
 * Gmail-style compose row: left label prefix (To/Cc/Bcc), a chips/select input
 * passed as children, and an optional ✕ button that hides the field.
 *
 * The `[&_.form-control-select]:!border-0` / `!shadow-none` overrides strip
 * the react-select's inline border and focus shadow so the row's own bottom
 * border is the only divider — matching the Gmail compose look. These rely on
 * `ReactCreatableSelectWrapper` applying the `form-control-select` class.
 */
export function RecipientRow({
  label,
  children,
  onRemove,
  trailing,
}: {
  label: string
  children: React.ReactNode
  /** Renders an X remove button on the right; used for Cc/Bcc. */
  onRemove?: () => void
  /** Optional inline content (e.g. Cc/Bcc toggles) appearing next to the chips. */
  trailing?: React.ReactNode
}) {
  return (
    <div className="flex items-center gap-2 py-0.5 px-2 border-b border-neutral-200 cursor-text [&_.form-control-select]:!border-0 [&_.form-control-select]:!shadow-none">
      <div className="text-sm text-neutral-500 w-12 flex-shrink-0 font-medium text-left">
        {label}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
      {trailing && <div className="flex-shrink-0">{trailing}</div>}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label}`}
          className="text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 size-6 rounded-full flex items-center justify-center flex-shrink-0"
        >
          <PiTrash className="size-4" />
        </button>
      )}
    </div>
  )
}

/**
 * Gmail-style "Cc" / "Bcc" toggle buttons. Only renders the buttons whose
 * corresponding field is hidden, so it disappears once both are revealed.
 */
export function CcBccToggles({
  showCc,
  showBcc,
  onShowCc,
  onShowBcc,
}: {
  showCc: boolean
  showBcc: boolean
  onShowCc: () => void
  onShowBcc: () => void
}) {
  if (showCc && showBcc) return null
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {!showCc && (
        <button
          type="button"
          onClick={onShowCc}
          className="text-xs text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 px-1.5 py-1 rounded"
        >
          Cc
        </button>
      )}
      {!showBcc && (
        <button
          type="button"
          onClick={onShowBcc}
          className="text-xs text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 px-1.5 py-1 rounded"
        >
          Bcc
        </button>
      )}
    </div>
  )
}
